import { useRef, useState } from 'react'

const MONTH_NAMES = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec']
const MAX_LANES = 3
const LANE_HEIGHT = 20
const COLLISION_THRESHOLD_PCT = 2.5

const daysInYear = (year) => (new Date(year, 11, 31) - new Date(year, 0, 1)) / 86400000 + 1
const dayOfYear = (date, year) => Math.floor((date - new Date(year, 0, 1)) / 86400000)
const dateFromPct = (pct, year) => {
    const total = daysInYear(year)
    const day = Math.round((pct / 100) * (total - 1))
    return new Date(year, 0, 1 + day)
}

export default function MilestoneTimeline({ year, milestones, onReschedule }) {
    const total = daysInYear(year)
    const sorted = [...milestones].sort((a, b) => a.start - b.start)
    const trackRef = useRef(null)
    const [hoveredId, setHoveredId] = useState(null)
    const [dragState, setDragState] = useState(null) // { id, pct }

    const laneLastPct = new Array(MAX_LANES).fill(-Infinity)
    const placed = sorted.map((m) => {
        const pct = dragState?.id === m._id ? dragState.pct : (dayOfYear(m.start, year) / total) * 100
        let lane = MAX_LANES - 1
        for (let i = 0; i < MAX_LANES; i++) {
            if (pct - laneLastPct[i] > COLLISION_THRESHOLD_PCT) {
                lane = i
                break
            }
        }
        laneLastPct[lane] = pct
        return { ...m, pct, lane }
    })

    const pctFromClientX = (clientX) => {
        const rect = trackRef.current.getBoundingClientRect()
        const raw = ((clientX - rect.left) / rect.width) * 100
        return Math.min(100, Math.max(0, raw))
    }

    const handlePointerDown = (e, milestone) => {
        if (!onReschedule) return
        e.preventDefault()
        const startPct = (dayOfYear(milestone.start, year) / total) * 100
        setDragState({ id: milestone._id, pct: startPct })

        const handleMove = (moveEvent) => {
            const pct = pctFromClientX(moveEvent.clientX)
            setDragState({ id: milestone._id, pct })
        }
        const handleUp = (upEvent) => {
            window.removeEventListener('mousemove', handleMove)
            window.removeEventListener('mouseup', handleUp)
            const pct = pctFromClientX(upEvent.clientX)
            setDragState(null)
            const newDate = dateFromPct(pct, year)
            if (newDate.getTime() !== new Date(milestone.start.getFullYear(), milestone.start.getMonth(), milestone.start.getDate()).getTime()) {
                onReschedule(milestone, newDate)
            }
        }
        window.addEventListener('mousemove', handleMove)
        window.addEventListener('mouseup', handleUp)
    }

    if (placed.length === 0) {
        return <p className="font-mono text-xs text-zinc-500">No milestones this year.</p>
    }

    const hovered = placed.find((m) => m._id === hoveredId)

    return (
        <div className="relative" style={{ height: 32 + MAX_LANES * LANE_HEIGHT }}>
            <div className="flex justify-between font-mono text-[10px] uppercase text-zinc-500 select-none">
                {MONTH_NAMES.map((m) => <span key={m}>{m}</span>)}
            </div>
            <div ref={trackRef} className="absolute inset-x-0 top-5 bottom-0">
                <div className="absolute inset-x-0 top-0 border-t-2 border-black dark:border-white" />
                {placed.map((m) => (
                    <div
                        key={m._id}
                        onMouseDown={(e) => handlePointerDown(e, m)}
                        onMouseEnter={() => setHoveredId(m._id)}
                        onMouseLeave={() => setHoveredId((id) => (id === m._id ? null : id))}
                        className={[
                            'absolute h-3 w-3 -translate-x-1/2 border-2 border-black dark:border-white transition-transform',
                            onReschedule ? 'cursor-grab active:cursor-grabbing' : '',
                            hoveredId === m._id || dragState?.id === m._id ? 'scale-150 z-10' : '',
                            dragState?.id === m._id ? 'opacity-70' : '',
                        ].join(' ')}
                        style={{
                            left: `${m.pct}%`,
                            top: 2 + m.lane * LANE_HEIGHT,
                            backgroundColor: m.category?.color || '#a1a1aa',
                        }}
                    />
                ))}
            </div>

            {hovered && !dragState && (
                <div
                    className="pointer-events-none absolute z-20 -translate-x-1/2 border-2 border-black dark:border-white bg-white dark:bg-zinc-950 px-2 py-1 text-xs font-mono text-black dark:text-white shadow-brutal-sm whitespace-nowrap"
                    style={{ left: `${hovered.pct}%`, top: 2 + hovered.lane * LANE_HEIGHT - 34 }}
                >
                    <span className="font-bold">{hovered.title}</span>
                    <span className="text-zinc-500"> — {hovered.start.toLocaleDateString()}</span>
                </div>
            )}
        </div>
    )
}
